import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBuilding,
  FaBullhorn,
  FaBullseye,
  FaChartPie,
  FaEdit,
  FaFlag,
  FaInbox,
  FaMapMarkerAlt,
  FaPenNib,
  FaSeedling,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { Badge, ErrorBox } from "./components/common";
import CampaignLeads from "./components/CampaignLeads";
import CampaignTable from "./components/CampaignTable";
import ContentBoard from "./components/ContentBoard";
import MetricsGrid from "./components/MetricsGrid";
import OrganicPosts from "./components/OrganicPosts";
import ProjectForm from "./components/ProjectForm";
import ProjectSummary from "./components/ProjectSummary";
import TenantOverview from "./components/TenantOverview";
import { errorMessage, hasService, staffDmApi } from "./dmApi";
import "./dm.css";

/**
 * One DM project: client details, content review (Day 1), organic posting (Day 2),
 * paid campaigns (Day 3) and daily responses (Day 4).
 */
function DMProjectWorkspace({ api = staffDmApi, basePath = "/dm-projects" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [content, setContent] = useState([]);
  const [posts, setPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const bump = () => setRefreshKey((k) => k + 1);

  const loadContent = useCallback(async () => setContent((await api.listContent(id)) || []), [api, id]);
  const loadPosts = useCallback(async () => setPosts((await api.listPosts(id)) || []), [api, id]);
  const loadCampaigns = useCallback(async () => setCampaigns((await api.listCampaigns(id)) || []), [api, id]);
  const loadProject = useCallback(async () => {
    const p = await api.getProject(id);
    setProject(p);
    return p;
  }, [api, id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const [p, m] = await Promise.all([api.getProject(id), api.meta()]);
        if (cancelled) return;
        setProject(p);
        setMeta(m);
        const [ov, c, ps, cs] = await Promise.all([
          api.overview(api.isStaff ? p.tenant_id : undefined).catch(() => null),
          api.listContent(id),
          api.listPosts(id),
          api.listCampaigns(id),
        ]);
        if (cancelled) return;
        setOverview(ov);
        setContent(c || []);
        setPosts(ps || []);
        setCampaigns(cs || []);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Could not load project"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const refresh = (...loaders) => async () => {
    await Promise.all([...loaders.map((fn) => fn()), loadProject()]);
    bump();
  };

  const updateStage = async (e) => {
    const Stage = e.target.value;
    try {
      setProject(await api.updateProject(project.Dm_Project_Id, { Stage }));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const removeProject = async () => {
    if (!window.confirm(`Delete project "${project.Project_Name}" with its content, posts, campaigns and responses?`)) return;
    try {
      await api.deleteProject(project.Dm_Project_Id);
      navigate(api.isStaff ? `${basePath}/client/${project.tenant_id}?tab=dm-projects` : basePath);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="dmp">
        <div className="dmp-card dmp-empty">Loading project…</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="dmp">
        <Link to={basePath}>← All projects</Link>
        <ErrorBox message={error || "Project not found"} />
      </div>
    );
  }

  const access = project.Dm_Access || overview?.Dm_Access;
  const organicAllowed = hasService(access, ["SEO", "SMO"]);
  const paidAllowed = hasService(access, ["SEM", "SMM"]);
  const pendingReview = content.filter((c) => c.Review_Status === "Internal Review" || c.Review_Status === "Client Review").length;
  const liveAds = campaigns.filter((c) => c.Status === "Active").length;

  const tabs = [
    { key: "overview", label: "Overview", icon: FaChartPie },
    { key: "content", label: "Content", icon: FaPenNib, count: pendingReview || null },
    organicAllowed
      ? { key: "organic", label: "Organic (SEO / SMO)", icon: FaSeedling, count: posts.length || null }
      : null,
    paidAllowed
      ? { key: "campaigns", label: "Campaigns (SEM / SMM)", icon: FaBullseye, count: liveAds || null }
      : null,
    { key: "responses", label: "Responses", icon: FaInbox },
  ].filter(Boolean);
  const activeTab = tabs.some((t) => t.key === tab) ? tab : "overview";
  const ActiveIcon = tabs.find((t) => t.key === activeTab)?.icon || FaChartPie;

  return (
    <div className="dmp dmp-workspace">
      <Link
        to={api.isStaff ? `${basePath}/client/${project.tenant_id}?tab=dm-projects` : basePath}
        className="dmp-back-link"
      >
        <FaArrowLeft aria-hidden="true" />
        {api.isStaff ? `${project.Tenant_Name || "Client"} projects` : "All projects"}
      </Link>

      <section className="dmp-hero">
        <div className="dmp-hero__main">
          <span className="dmp-hero__icon" aria-hidden="true">
            <FaBullhorn />
          </span>
          <div className="dmp-hero__text">
            <h1 className="dmp-hero__title">{project.Project_Name}</h1>
            <div className="dmp-hero__badges">
              <Badge value={project.Priority} />
              {project.Short_Code ? <span className="dmp-hero__code">{project.Short_Code}</span> : null}
            </div>
            <ul className="dmp-hero__meta">
              {api.isStaff && project.Tenant_Name ? (
                <li>
                  <FaBuilding aria-hidden="true" />
                  {project.Tenant_Name}
                </li>
              ) : null}
              <li>
                <FaMapMarkerAlt aria-hidden="true" />
                {project.Location || "No location"}
              </li>
              {project.Assigned_To_Name ? (
                <li>
                  <FaUser aria-hidden="true" />
                  {project.Assigned_To_Name}
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div className="dmp-hero__buttons">
          <button
            type="button"
            className={`dmp-icon-btn${editing ? " is-active" : ""}`}
            onClick={() => setEditing((v) => !v)}
            title={editing ? "Close edit" : "Edit project"}
            aria-label={editing ? "Close edit" : "Edit project"}
          >
            {editing ? <FaTimes aria-hidden="true" /> : <FaEdit aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="dmp-icon-btn dmp-icon-btn--danger"
            onClick={removeProject}
            title="Delete project"
            aria-label="Delete project"
          >
            <FaTrash aria-hidden="true" />
          </button>
        </div>
      </section>

      <div className="dmp-toolbar">
        <label className="dmp-toolbar__field">
          <span className="dmp-toolbar__label">
            <ActiveIcon aria-hidden="true" />
            View
          </span>
          <select value={activeTab} onChange={(e) => setTab(e.target.value)} aria-label="Project section">
            {tabs.map((t) => (
              <option key={t.key} value={t.key}>
                {t.count ? `${t.label} (${t.count})` : t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="dmp-toolbar__field">
          <span className="dmp-toolbar__label">
            <FaFlag aria-hidden="true" />
            Stage
          </span>
          <select value={project.Stage} onChange={updateStage} aria-label="Project stage">
            {(meta?.stages || [project.Stage]).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {access && !access.enabled ? (
        <div className="dmp-notice">
          Digital Marketing is not active for this client
          {access.allowed_by_business_type ? " (no Digital Marketing service subscribed)" : " (not available for this business type)"}.
          Existing data is read-only until the service is enabled.
        </div>
      ) : null}
      <ErrorBox message={error} />

      {editing ? (
        <div className="dmp-card">
          <h3 className="dmp-card__title">Edit project</h3>
          <ProjectForm
            api={api}
            meta={meta}
            project={project}
            onCancel={() => setEditing(false)}
            onSaved={(saved) => {
              setProject(saved);
              setEditing(false);
            }}
          />
        </div>
      ) : null}

      {activeTab === "overview" ? (
        <>
          <ProjectSummary api={api} projectId={id} refreshKey={refreshKey} compact />
          <TenantOverview
            overview={overview}
            project={project}
            onManageAccounts={api.isStaff ? () => navigate(`/tenants/${project.tenant_id}/view`) : undefined}
          />
        </>
      ) : null}

      {activeTab === "content" ? (
        <ContentBoard api={api} meta={meta} projectId={id} content={content} onChange={refresh(loadContent)} />
      ) : null}

      {activeTab === "organic" ? (
        <OrganicPosts
          api={api}
          meta={meta}
          projectId={id}
          posts={posts}
          content={content}
          accounts={overview?.Social_Accounts || []}
          onChange={refresh(loadPosts)}
        />
      ) : null}

      {activeTab === "campaigns" ? (
        <CampaignTable
          api={api}
          meta={meta}
          projectId={id}
          campaigns={campaigns}
          content={content}
          access={access}
          onChange={refresh(loadCampaigns)}
        />
      ) : null}

      {activeTab === "responses" ? (
        <>
          <ProjectSummary api={api} projectId={id} refreshKey={refreshKey} />
          <MetricsGrid api={api} meta={meta} projectId={id} posts={posts} campaigns={campaigns} onSaved={bump} />
          <CampaignLeads api={api} projectId={id} campaigns={campaigns} refreshKey={refreshKey} />
        </>
      ) : null}
    </div>
  );
}

export default DMProjectWorkspace;
